import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginatedResult, PaginationParams } from '@core/common/models/pagination.models';
import { PreparationSettingsApiInput } from '@shared/ui/preparation-editor/preparation-editor.models';
import { CategoryMutationResult, ProductCategory } from '@features/catalog/models/product-category.models';
import { CategoryApiService } from '@features/catalog/data-access/category-api.service';

@Injectable()
export class CatalogCategoryService {
  constructor(private readonly categoryApi: CategoryApiService) {}

  list(
    filters?: { activeOnly?: boolean | null },
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductCategory>> {
    return this.categoryApi.list(
      { activeOnly: filters?.activeOnly === undefined ? true : filters.activeOnly },
      pagination
    );
  }

  searchByName(name: string): Observable<PaginatedResult<ProductCategory>> {
    return this.categoryApi.searchByName(name);
  }

  getById(id: string): Observable<ProductCategory | null> {
    return this.categoryApi.getById(id);
  }

  create(input: {
    name: string;
    position: number;
    parentId?: string | null;
    active?: boolean;
    iconName?: string | null;
    fulfillmentLeadTimeMode?: 'inherit' | 'override' | null;
    fulfillmentLeadTimeDays?: number | null;
  } & PreparationSettingsApiInput): Observable<CategoryMutationResult> {
    return this.categoryApi.create(input);
  }

  update(input: {
    id: string;
    name?: string;
    active?: boolean;
    position?: number;
    parentId?: string | null;
    iconName?: string | null;
    fulfillmentLeadTimeMode?: 'inherit' | 'override' | null;
    fulfillmentLeadTimeDays?: number | null;
  } & PreparationSettingsApiInput): Observable<CategoryMutationResult> {
    return this.categoryApi.update(input);
  }

  bulkUpdatePreparation(
    ids: readonly string[],
    input: PreparationSettingsApiInput
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.categoryApi.bulkUpdatePreparation(ids, input);
  }

  setHalfAndHalf(
    categoryId: string,
    enabled: boolean
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.categoryApi.setHalfAndHalf(categoryId, enabled);
  }

  destroy(id: string): Observable<CategoryMutationResult> {
    return this.categoryApi.destroy(id);
  }

  deactivate(id: string): Observable<CategoryMutationResult> {
    return this.categoryApi.destroy(id);
  }

  reactivate(id: string): Observable<CategoryMutationResult> {
    return this.categoryApi.updateActive(id, true);
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.categoryApi.permanentlyDelete(id);
  }

  move(input: {
    id: string;
    position: number;
    parentId?: string | null;
  }): Observable<CategoryMutationResult> {
    return this.categoryApi.move(input);
  }
}
